import { Injectable } from '@angular/core';
import { from, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { collection, collectionData, Firestore, getDocs, limit, orderBy, query, where } from "@angular/fire/firestore";
import { Timestamp } from "firebase/firestore";
import { ScheduleGame } from "../model/broadcast/game";

// Bounds of a league's scraped schedule, used by the admin UI to spot leagues
// that have no games left to poll (and can therefore be archived).
export interface ScheduleSummary {
  hasSchedule: boolean;
  next?: Date;
  last?: Date;
}

// Reads the per-league schedule that ball-score-server scrapes from WBSC into
// the `leagues/{leagueId}/scheduled-games` subcollection (one doc per game).
// Replaces the old WbscService.fetchScheduledGames proxy path.
@Injectable({
  providedIn: 'root'
})
export class SchedulesService {

  constructor(
    private firestore: Firestore,
  ) { }

  getSchedule(leagueId: string): Observable<ScheduleGame[]> {
    const ref = collection(this.firestore, `leagues/${leagueId}/scheduled-games`);
    return collectionData(ref) as Observable<ScheduleGame[]>;
  }

  // Two limit(1) reads instead of pulling the whole subcollection: the earliest
  // game still ahead of us, and the last game of the schedule overall. Both are
  // covered by the automatic single-field index on `scheduled`.
  getScheduleSummary(leagueId: string): Observable<ScheduleSummary> {
    const ref = collection(this.firestore, `leagues/${leagueId}/scheduled-games`);
    const next = getDocs(query(ref, where('scheduled', '>=', Timestamp.now()), orderBy('scheduled', 'asc'), limit(1)));
    const last = getDocs(query(ref, orderBy('scheduled', 'desc'), limit(1)));
    return from(Promise.all([next, last])).pipe(
      map(([nextSnap, lastSnap]) => ({
        hasSchedule: !lastSnap.empty,
        next: nextSnap.empty ? undefined : this.toDate(nextSnap.docs[0].data()['scheduled']),
        last: lastSnap.empty ? undefined : this.toDate(lastSnap.docs[0].data()['scheduled']),
      })),
    );
  }

  private toDate(value: any): Date | undefined {
    return value?.toDate ? value.toDate() : undefined;
  }
}
